import React, { useCallback, useMemo, useState } from "react";
import type { StockSearchBrief } from "fin-web-components";
import { useUserHoldingsEntitlement } from "../../components/fin-web/UserHoldingsEntitlementContext";
import { useUserHoldingsNavigate } from "../../components/fin-web/useUserHoldingsNavigate";
import { AddWatchlistItemModal } from "../user_holdings/AddWatchlistItemModal";
import {
  HoldingsActionSuccessModal,
  HoldingsNoPermissionModal,
} from "../user_holdings/HoldingsActionSuccessModal";
import { OpenBuyModal } from "../user_holdings/OpenBuyModal";
import { UserHoldingsApi } from "../user_holdings/UserHoldingsApi";
import { UserWatchlistApi } from "../user_holdings/UserWatchlistApi";
import type { UserWatchlistGroupDTO } from "../user_holdings/UserWatchlistType";
import "../user_holdings/UserHoldingsPage.css";

type SuccessKind = "openBuy" | "watchlist" | null;

export function TacticsHoldingsQuickActions(props: {
  spaceId: string;
  stockCode: string;
  stockName: string;
  lastPrice: number | null | undefined;
}): JSX.Element | null {
  const { spaceId, stockCode, stockName, lastPrice } = props;
  const { holdingsModule, holdingsEntitled } = useUserHoldingsEntitlement();
  const goHoldings = useUserHoldingsNavigate(spaceId);

  const [noPermOpen, setNoPermOpen] = useState(false);
  const [openBuyOpen, setOpenBuyOpen] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  const [holdingsCount, setHoldingsCount] = useState(0);
  const [watchGroups, setWatchGroups] = useState<UserWatchlistGroupDTO[]>([]);
  const [successKind, setSuccessKind] = useState<SuccessKind>(null);

  const seedStock: StockSearchBrief | null = useMemo(() => {
    const code = stockCode.trim();
    if (!code) {
      return null;
    }
    return {
      id: 0,
      code,
      name: (stockName || stockCode).trim(),
    };
  }, [stockCode, stockName]);

  const loadWatchGroups = useCallback(async () => {
    const g = await UserWatchlistApi.listGroups(spaceId);
    setWatchGroups(Array.isArray(g?.rows) ? g!.rows! : []);
  }, [spaceId]);

  const guardOrRun = useCallback(
    (run: () => void) => {
      if (!holdingsEntitled) {
        setNoPermOpen(true);
        return;
      }
      run();
    },
    [holdingsEntitled]
  );

  const onClickOpenBuy = useCallback(() => {
    guardOrRun(() => {
      void (async () => {
        const list = await UserHoldingsApi.listHoldings(spaceId);
        const rows = Array.isArray(list?.rows) ? list!.rows! : [];
        setHoldingsCount(rows.filter((r) => r.holdingStatus === "OPEN").length);
        setOpenBuyOpen(true);
      })();
    });
  }, [guardOrRun, spaceId]);

  const onClickWatch = useCallback(() => {
    guardOrRun(() => {
      void (async () => {
        await loadWatchGroups();
        setWatchOpen(true);
      })();
    });
  }, [guardOrRun, loadWatchGroups]);

  if (!holdingsModule) {
    return null;
  }

  const successTitle = successKind === "openBuy" ? "建仓成功" : "添加成功";
  const successDesc =
    successKind === "openBuy"
      ? `「${stockName || stockCode}」已加入持仓，可前往我的持仓查看与管理。`
      : `「${stockName || stockCode}」已加入自选，可前往我的持仓查看与管理。`;

  return (
    <>
      <div className="tactics-stock-quick-actions">
        <button type="button" className="tactics-stock-quick-action" onClick={onClickOpenBuy}>
          添加持仓
        </button>
        <button type="button" className="tactics-stock-quick-action" onClick={onClickWatch}>
          加自选
        </button>
      </div>

      <HoldingsNoPermissionModal open={noPermOpen} onClose={() => setNoPermOpen(false)} />

      <OpenBuyModal
        spaceId={spaceId}
        open={openBuyOpen}
        seedStock={seedStock}
        seedBuyPrice={typeof lastPrice === "number" && lastPrice > 0 ? lastPrice : null}
        holdingsCount={holdingsCount}
        silentSuccess
        onClose={(ok) => {
          setOpenBuyOpen(false);
          if (ok) {
            setSuccessKind("openBuy");
          }
        }}
      />

      <AddWatchlistItemModal
        spaceId={spaceId}
        open={watchOpen}
        groups={watchGroups}
        defaultSelectedGroupId={
          watchGroups.length ? String(watchGroups[0]?.id ?? "") : ""
        }
        seedStock={seedStock}
        silentSuccess
        onRefreshGroups={() => void loadWatchGroups()}
        onClose={(ok) => {
          setWatchOpen(false);
          if (ok) {
            setSuccessKind("watchlist");
          }
        }}
      />

      <HoldingsActionSuccessModal
        open={successKind != null}
        title={successTitle}
        description={successDesc}
        onStay={() => setSuccessKind(null)}
        onGoHoldings={() => {
          setSuccessKind(null);
          goHoldings();
        }}
      />
    </>
  );
}
